import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useLanguage } from "@/hooks/useLanguage";
import LanguageSelector from "@/components/LanguageSelector";
import { 
  Eye, 
  EyeOff, 
  LogIn, 
  ExternalLink, 
  CheckCircle2
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email,
        password,
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success("Login successful!", {
        description: `Welcome back, ${response.data.user.name}!`,
      });
      
      navigate("/admin/dashboard");
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Login failed", {
        description: error.response?.data?.detail || "Invalid email or password",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Features showcase */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 login-hero-overlay"></div>
        <img
          src="https://images.unsplash.com/photo-1665538941694-e1705e52d1b2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxmcmVzaCUyMGVnZ3MlMjBmYXJtJTIwbWluaW1hbGlzdHxlbnwwfHx8fDE3NjgwMzYyNTV8MA&ixlib=rb-4.1.0&q=85"
          alt="Fresh eggs"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <div className="bg-white rounded-xl p-4 shadow-lg mb-6">
            <img
              src="/logo.png"
              alt="LitSpark Egg Distributors Demo Logo"
              className="w-24 h-24"
            />
          </div>
          <h1 className="text-3xl font-semibold text-white mb-2 text-center tracking-tight">
            {t.login.title}
          </h1>
          <p className="text-blue-100 text-base text-center max-w-md font-light mb-6">
            {t.login.subtitle}
          </p>
          
          {/* Features List */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-white font-semibold mb-4 text-center">{t.login.featuresTitle}</h3>
            <div className="grid grid-cols-2 gap-3">
              {t.login.features.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-white/90">
                  <CheckCircle2 size={16} className="text-blue-300 flex-shrink-0" />
                  <span className="text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
          
          <a
            href="https://litspark.solutions/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-white hover:text-blue-200 font-medium transition-colors"
          >
            {t.languageModal.changeLanguage === "Change Language" ? "Know More about LitSpark" : "LitSpark ಬಗ್ಗೆ ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ"}
            <ExternalLink size={16} />
          </a>
        </div>
      </div>

      {/* Right side - Login form */}
      <div className="w-full lg:w-1/2 flex flex-col bg-gradient-to-br from-blue-50 to-white">
        {/* Language selector for login page */}
        <div className="flex justify-end p-4">
          <LanguageSelector />
        </div>
        
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-md border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardHeader className="space-y-4 text-center pb-2">
              {/* Mobile logo */}
              <div className="lg:hidden flex flex-col items-center mb-4">
                <div className="bg-white rounded-xl p-3 shadow-md mb-3">
                  <img
                    src="/logo.png"
                    alt="LitSpark Egg Distributors Demo Logo"
                    className="w-20 h-20"
                  />
                </div>
                <a
                  href="https://litspark.solutions/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-primary hover:text-primary-600 text-sm font-medium transition-colors"
                >
                  {t.languageModal.changeLanguage === "Change Language" ? "Know More about LitSpark" : "LitSpark Solutions"}
                  <ExternalLink size={14} />
                </a>
              </div>
              <h2 className="text-2xl font-semibold text-primary-950 tracking-tight">
                {t.login.tryDemo}
              </h2>
              <p className="text-muted-foreground text-sm font-light">
                {t.login.signInSubtitle}
              </p>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-primary-900">
                    {t.login.emailLabel}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t.login.emailPlaceholder}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11 bg-white/50 border-blue-200 focus:border-primary focus:ring-primary/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-primary-900">
                    {t.login.passwordLabel}
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder={t.login.passwordPlaceholder}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-10 bg-white/50 border-blue-200 focus:border-primary focus:ring-primary/20"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                      data-testid="toggle-password-visibility"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-submit-button"
                  className="w-full h-11 rounded-full bg-primary hover:bg-primary-600 text-white font-medium btn-primary"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t.login.signingIn}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <LogIn size={18} />
                      {t.login.signIn}
                    </span>
                  )}
                </Button>
              </form>
              
              {/* WhatsApp Button */}
              <div className="mt-5 pt-5 border-t border-border/50 text-center">
                <p className="text-sm text-muted-foreground mb-3">
                  {t.login.whatsappText}
                </p>
                <a
                  href="https://api.whatsapp.com/send/?phone=918884445404&text=Hi&type=phone_number&app_absent=0"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full h-11 rounded-full bg-[#25D366] hover:bg-[#128C7E] text-white font-medium inline-flex items-center justify-center gap-2 transition-colors"
                  data-testid="whatsapp-demo-btn"
                >
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  {t.login.whatsappButton}
                </a>
              </div>
              
              {/* Footer on mobile */}
              <div className="lg:hidden mt-6 pt-4 border-t border-border/50 text-center">
                <p className="text-muted-foreground text-xs">
                  © 2026{" "}
                  <a 
                    href="https://litspark.solutions/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    LitSpark Solutions Private Limited
                  </a>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Login;
